// TikTok Pixel loader and tracker with consent gating.
// Official TikTok Pixel code - only loads if explicitly enabled via env/consent.

let pixelInitialized = false;

function captureTtclidIfPresent() {
  try {
    const url = new URL(window.location.href);
    const ttclid = url.searchParams.get('ttclid');
    if (!ttclid) return;
    // Store in first-party cookie for 7 days; adjust as needed.
    document.cookie = `ttclid=${ttclid}; Path=/; Max-Age=${60 * 60 * 24 * 7}; SameSite=Lax`;
  } catch (_) {
    // no-op
  }
}

export function initTikTokPixel(consent = false) {
  console.log('[TikTok Pixel] initTikTokPixel called with consent:', consent);
  
  if (!consent) {
    console.log('[TikTok Pixel] Consent not granted, skipping initialization');
    return;
  }
  
  if (pixelInitialized) {
    console.log('[TikTok Pixel] Already initialized, skipping');
    return;
  }

  const pixelId = process.env.REACT_APP_TIKTOK_PIXEL_ID;
  console.log('[TikTok Pixel] Pixel ID from env:', pixelId);
  
  if (!pixelId) {
    console.warn('[TikTok Pixel] No pixel ID found in REACT_APP_TIKTOK_PIXEL_ID');
    return;
  }

  // TikTok Pixel Code - Official implementation
  (function (w, d, t) {
    w.TiktokAnalyticsObject = t;
    var ttq = w[t] = w[t] || [];
    ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie", "holdConsent", "revokeConsent", "grantConsent"];
    ttq.setAndDefer = function (t, e) {
      t[e] = function () {
        t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
      };
    };
    for (var i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
    ttq.instance = function (t) {
      for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++) ttq.setAndDefer(e, ttq.methods[n]);
      return e;
    };
    ttq.load = function (e, n) {
      var r = "https://analytics.tiktok.com/i18n/pixel/events.js", o = n && n.partner;
      ttq._i = ttq._i || {};
      ttq._i[e] = [];
      ttq._i[e]._u = r;
      ttq._t = ttq._t || {};
      ttq._t[e] = +new Date();
      ttq._o = ttq._o || {};
      ttq._o[e] = n || {};
      n = document.createElement("script");
      n.type = "text/javascript";
      n.async = !0;
      n.src = r + "?sdkid=" + e + "&lib=" + t;
      e = document.getElementsByTagName("script")[0];
      e.parentNode.insertBefore(n, e);
    };

    // Load pixel with ID and send initial page event
    console.log('[TikTok Pixel] Loading pixel with ID: D4AAOGRC77U1PLJOHCN0');
    ttq.load('D4AAOGRC77U1PLJOHCN0');
    ttq.page();
    console.log('[TikTok Pixel] Initial page event sent');
  })(window, document, 'ttq');

  pixelInitialized = true;
  console.log('[TikTok Pixel] Initialization complete. Check window.ttq in console.');

  // Optionally capture ttclid for attribution (only after consent)
  captureTtclidIfPresent();
}

// SHA-256 hash function for PII data
async function sha256Hash(text) {
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(text.toLowerCase().trim());
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  } catch (error) {
    console.error('[TikTok Pixel] Error hashing:', error);
    return null;
  }
}

// Identify user with hashed email (only call after consent)
export async function identifyTikTokUser(email, userId = null) {
  if (!pixelInitialized) {
    console.log('[TikTok Pixel] Cannot identify - pixel not initialized');
    return;
  }
  
  try {
    if (!window.ttq || typeof window.ttq.identify !== 'function') {
      console.warn('[TikTok Pixel] Cannot identify - ttq.identify not available');
      return;
    }

    const hashedEmail = email ? await sha256Hash(email) : null;
    const hashedUserId = userId ? await sha256Hash(String(userId)) : null;

    const identifyData = {};
    if (hashedEmail) identifyData.email = hashedEmail;
    if (hashedUserId) identifyData.external_id = hashedUserId;

    if (Object.keys(identifyData).length > 0) {
      console.log('[TikTok Pixel] Identifying user (hashed data)');
      window.ttq.identify(identifyData);
    }
  } catch (error) {
    console.error('[TikTok Pixel] Error identifying user:', error);
  }
}

// Track CompleteRegistration event (new signups)
export function trackTikTokRegistration(contentName = 'Account') {
  try {
    if (window.ttq && typeof window.ttq.track === 'function') {
      console.log('[TikTok Pixel] Tracking CompleteRegistration');
      window.ttq.track('CompleteRegistration', {
        contents: [{
          content_type: 'product',
          content_name: contentName
        }],
        value: 0,
        currency: 'EUR'
      });
    } else {
      console.warn('[TikTok Pixel] Cannot track - window.ttq not available');
    }
  } catch (error) {
    console.error('[TikTok Pixel] Error tracking registration:', error);
  }
}

// Track Purchase event (paid subscriptions)
export function trackTikTokPurchase(subscriptionType = 'Subscription') {
  try {
    if (window.ttq && typeof window.ttq.track === 'function') {
      console.log('[TikTok Pixel] Tracking Purchase:', subscriptionType);
      window.ttq.track('CompletePayment', {
        contents: [{
          content_type: 'product',
          content_name: subscriptionType
        }],
        value: 1,
        currency: 'EUR'
      });
    } else {
      console.warn('[TikTok Pixel] Cannot track - window.ttq not available');
    }
  } catch (error) {
    console.error('[TikTok Pixel] Error tracking purchase:', error);
  }
}

// Generic event tracking
export function trackTikTok(eventName, params = {}) {
  try {
    if (window.ttq && typeof window.ttq.track === 'function') {
      console.log('[TikTok Pixel] Tracking event:', eventName, params);
      window.ttq.track(eventName, params);
    } else {
      console.warn('[TikTok Pixel] Cannot track - window.ttq not available');
    }
  } catch (error) {
    console.error('[TikTok Pixel] Error tracking event:', error);
  }
}



